import QRCode from "qrcode";
import { type FormEvent, useEffect, useState } from "react";
import {
  deleteAuthenticationMethod,
  startPhoneEnrollment,
  startTotpEnrollment,
  type TotpEnrollment,
  verifyOtpEnrollment,
} from "../api.ts";
import { Dialog } from "../components/Dialog.tsx";
import { Note, type Status } from "../components/ui.tsx";
import { errorMessage } from "../errors.ts";

export type MfaKind = "totp" | "phone";

type Props = {
  /** 登録が終わったら解除する、今の多要素認証（認証アプリかSMSのどちらか1つにする） */
  replaceIds: string[];
  onClose: () => void;
  onDone: (status: Status) => void;
  /** step-up が要るエラーなら true（呼び出し元がダイアログを閉じる） */
  onError: (error: unknown) => boolean;
};

const OTP_PATTERN = /^\d{6}$/;

/** 新しい方法の確認が済んでから、前の方法を解除する（途中で失敗しても MFA が無くならないように） */
async function finishReplacing(
  newId: string,
  replaceIds: string[],
  label: string
): Promise<Status> {
  const targets = replaceIds.filter((id) => id !== newId);
  try {
    for (const id of targets) await deleteAuthenticationMethod(id);
  } catch {
    return {
      tone: "error",
      text: `${label}を登録しましたが、以前の方法を解除できませんでした。以前の方法を削除してください。`,
    };
  }
  return { tone: "success", text: `${label}を登録しました。` };
}

function OtpField({
  value,
  onChange,
  describedBy,
}: {
  value: string;
  onChange: (v: string) => void;
  describedBy: string;
}) {
  return (
    <div className="field">
      <label htmlFor="otp-code" className="field-label">
        確認コード（6 桁）
      </label>
      <input
        id="otp-code"
        className="input input--code"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={value}
        aria-describedby={describedBy}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
      />
    </div>
  );
}

function ErrorText({ error }: { error: string | null }) {
  return error === null ? null : (
    <p className="form-error" role="alert">
      {error}
    </p>
  );
}

/** 認証アプリ（TOTP）の登録: QR コードを読み取り、表示された確認コードを入れる */
export function TotpEnrollDialog({ replaceIds, onClose, onDone, onError }: Props) {
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: 開いたときに 1 回だけ登録を始める
  useEffect(() => {
    let active = true;
    startTotpEnrollment()
      .then(async (result) => {
        const svg = await QRCode.toString(result.barcode_uri, { type: "svg", margin: 1 });
        if (!active) return;
        setEnrollment(result);
        setQr(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
      })
      .catch((e: unknown) => {
        if (!active || onError(e)) return;
        setError(errorMessage(e, "認証アプリの登録を始められませんでした。"));
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (enrollment === null) return;
    if (!OTP_PATTERN.test(code)) {
      setError("6 桁の確認コードを入力してください。");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await verifyOtpEnrollment(enrollment.id, enrollment.auth_session, code);
      onDone(await finishReplacing(enrollment.id, replaceIds, "認証アプリ"));
    } catch (err) {
      if (onError(err)) return;
      setError(errorMessage(err, "確認コードが正しくないか、有効期限が切れています。"));
      setBusy(false);
    }
  };

  return (
    <Dialog title="認証アプリを設定" onClose={onClose} busy={busy && enrollment !== null}>
      <form className="dialog__form" onSubmit={onSubmit} noValidate>
        <p className="dialog__text" id="totp-help">
          Google Authenticator などの認証アプリで QR コードを読み取り、アプリに表示された 6
          桁の確認コードを入力してください。
        </p>
        {qr !== null ? (
          <div className="qr">
            <img src={qr} alt="認証アプリに登録する QR コード" width={180} height={180} />
            {enrollment?.manual_input_code ? (
              <div className="qr__manual">
                <span>読み取れない場合は、次のキーを入力してください</span>
                <code>{enrollment.manual_input_code}</code>
              </div>
            ) : null}
          </div>
        ) : enrollment === null && error === null ? (
          <p className="load-state" role="status">
            準備しています…
          </p>
        ) : null}
        {enrollment !== null ? (
          <OtpField value={code} onChange={setCode} describedBy="totp-help" />
        ) : null}
        {replaceIds.length > 0 ? <Note>登録が完了すると、今の方法は解除されます。</Note> : null}
        <ErrorText error={error} />
        <div className="dialog__actions">
          <button
            type="button"
            className="button button--secondary"
            onClick={onClose}
            disabled={busy && enrollment !== null}
          >
            キャンセル
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={busy || enrollment === null}
          >
            {busy && enrollment !== null ? "確認しています…" : "確認して登録"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** 国内の番号（090-1234-5678）は +81 に直す。国番号付きはそのまま。形が違えば null */
export function normalizePhoneNumber(input: string): string | null {
  const compact = input.replace(/[\s\-()（）ー－]/g, "");
  if (/^\+[1-9]\d{6,14}$/.test(compact)) return compact;
  if (/^0\d{9,10}$/.test(compact)) return `+81${compact.slice(1)}`;
  return null;
}

/** SMS の登録: 電話番号に確認コードを送り、届いたコードを入れる */
export function SmsEnrollDialog({ replaceIds, onClose, onDone, onError }: Props) {
  const [phone, setPhone] = useState("");
  const [sent, setSent] = useState<{ id: string; authSession: string; phone: string } | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSend = async (e: FormEvent) => {
    e.preventDefault();
    const normalized = normalizePhoneNumber(phone);
    if (normalized === null) {
      setError("電話番号の形式が正しくありません。");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await startPhoneEnrollment(normalized);
      setSent({ id: result.id, authSession: result.auth_session, phone: normalized });
      setCode("");
    } catch (err) {
      if (onError(err)) return;
      setError(errorMessage(err, "確認コードを送信できませんでした。電話番号を確認してください。"));
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (e: FormEvent) => {
    e.preventDefault();
    if (sent === null) return;
    if (!OTP_PATTERN.test(code)) {
      setError("6 桁の確認コードを入力してください。");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await verifyOtpEnrollment(sent.id, sent.authSession, code);
      onDone(await finishReplacing(sent.id, replaceIds, "SMS"));
    } catch (err) {
      if (onError(err)) return;
      setError(errorMessage(err, "確認コードが正しくないか、有効期限が切れています。"));
      setBusy(false);
    }
  };

  const actions = (submitLabel: string) => (
    <div className="dialog__actions">
      <button type="button" className="button button--secondary" onClick={onClose} disabled={busy}>
        キャンセル
      </button>
      <button type="submit" className="button button--primary" disabled={busy}>
        {busy ? "処理中…" : submitLabel}
      </button>
    </div>
  );

  return (
    <Dialog title="SMS を設定" onClose={onClose} busy={busy}>
      {sent === null ? (
        <form className="dialog__form" onSubmit={onSend} noValidate>
          <div className="field">
            <label htmlFor="phone-number" className="field-label">
              携帯電話番号
            </label>
            <input
              id="phone-number"
              type="tel"
              className="input"
              autoComplete="tel"
              placeholder="090-1234-5678"
              value={phone}
              aria-describedby="phone-help"
              onChange={(e) => setPhone(e.target.value)}
            />
            <span id="phone-help" className="help">
              この番号に確認コードを SMS で送ります。海外の番号は +1
              のように国番号から入力してください。
            </span>
          </div>
          {replaceIds.length > 0 ? <Note>登録が完了すると、今の方法は解除されます。</Note> : null}
          <ErrorText error={error} />
          {actions("確認コードを送信")}
        </form>
      ) : (
        <form className="dialog__form" onSubmit={onVerify} noValidate>
          <p className="dialog__text" id="sms-help">
            {sent.phone} に送信した 6 桁の確認コードを入力してください。
          </p>
          <OtpField value={code} onChange={setCode} describedBy="sms-help" />
          <button
            type="button"
            className="link-button"
            onClick={() => {
              setSent(null);
              setError(null);
            }}
            disabled={busy}
          >
            電話番号を変更する
          </button>
          <ErrorText error={error} />
          {actions("確認して登録")}
        </form>
      )}
    </Dialog>
  );
}
